/**
 * In-memory holding pen for product events (the /api/track-event beacon) fired while the visitor
 * has not yet answered the cookie banner.
 *
 * Why: the banner is shown on first paint, so a visitor who lands on a job page and accepts a few
 * seconds later used to lose that job's job_view for good (job_view fires once per job on mount,
 * before consent), while the later Apply click was recorded. That produced funnels with an apply
 * and no view.
 *
 * Privacy contract (keep it): events are held in JS memory only (never storage, never sent) and
 * are sent only if the visitor then grants analytics consent. On rejection, or when the page
 * unloads, they are simply discarded. Only visitors with NO stored choice are queued; an explicit
 * rejection is never queued. Flushed events carry pre_consent_delay_ms so the dashboards can tell
 * them apart from live ones.
 */

import { isClientAnalyticsAllowed } from '../cookie-consent/client-analytics-allowed';
import { JC_ANALYTICS_CONSENT_REJECTED_EVENT } from '../cookie-consent/constants';
import { isClientAnalyticsPendingConsent } from '../cookie-consent/client-analytics-pending-consent';
import { sendTrackEventBeacon } from './track-event-beacon';

const MAX_QUEUED = 25;
/** A decision this late is no longer "the visit I just started"; drop rather than backdate. */
const MAX_AGE_MS = 5 * 60 * 1000;

type QueuedEvent = { eventName: string; params: Record<string, unknown>; queuedAt: number };

let queue: QueuedEvent[] = [];

export function queuePreConsentProductEvent(eventName: string, params: Record<string, unknown>): void {
  if (!isClientAnalyticsPendingConsent()) {
    // A choice exists (or we're not in a trackable environment): nothing may be held over.
    queue = [];
    return;
  }
  if (queue.length >= MAX_QUEUED) queue.shift();
  queue.push({ eventName, params, queuedAt: Date.now() });
}

/** Call right after analytics consent is granted. Sends what was held, then empties the queue. */
export function flushPreConsentProductEvents(): void {
  const pending = queue;
  queue = [];
  if (pending.length === 0 || !isClientAnalyticsAllowed()) return;
  const now = Date.now();
  for (const item of pending) {
    const delay = now - item.queuedAt;
    if (delay > MAX_AGE_MS) continue;
    sendTrackEventBeacon(item.eventName, { ...item.params, pre_consent_delay_ms: delay });
  }
}

/** Call when the visitor rejects analytics. */
export function discardPreConsentProductEvents(): void {
  queue = [];
}

// Island routes never hydrate the React banner, so its vanilla-JS fallback announces a rejection
// with this event instead of calling discardPreConsentProductEvents() directly.
if (typeof document !== 'undefined') {
  document.addEventListener(JC_ANALYTICS_CONSENT_REJECTED_EVENT, discardPreConsentProductEvents);
}
